import React, {useEffect, useState} from 'react';
import * as Sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import Analytics from '../utils/Analytics';
import ActionButton from './ActionButton';
import logo from '../projects/emailConfirmation-0424/assets/logo@3x.png';
import '../projects/emailConfirmation-0424/screens/EmailConfirmation.css';

/** A lazy route's chunk didn't download (offline, or a deploy removed the old file) vs. a screen that threw. */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && /dynamically imported module|Importing a module script failed/i.test(error.message);

const autoReloadKey = (pathname: string) => `routeAutoReload:${pathname}`;

/**
 * Whether to reload by itself, recording the attempt. A tab left open across a deploy asks for chunk files the deploy
 * deleted, and a reload fetches the new ones, so most people never need to see the error. Each path gets one
 * automatic reload per tab session: when that doesn't help (offline, a broken deploy) the next failure shows Try
 * again, however long the failed download took. Without sessionStorage there's no guard, so no automatic reload.
 */
export const claimAutoReload = (pathname: string): boolean => {
  try {
    if (window.sessionStorage.getItem(autoReloadKey(pathname))) {
      return false;
    }
    window.sessionStorage.setItem(autoReloadKey(pathname), '1');
    return true;
  } catch {
    return false;
  }
};

function RouteLoadFailedScreen({error}: {error: unknown}) {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const chunkLoad = isChunkLoadError(error);
  // A chunk failure renders nothing until the effect decides whether to reload, so the error doesn't flash first.
  // The claim writes sessionStorage, so it runs in the effect: StrictMode calls state initializers twice.
  const [showError, setShowError] = useState(!chunkLoad);

  useEffect(() => {
    const autoReload = chunkLoad && claimAutoReload(pathname);
    const name = error instanceof Error ? error.name : 'unknown';
    Analytics.track('[Web Payments] Route Load Failed', {path: pathname, chunkLoad, autoReload, errorName: name});
    if (autoReload) {
      window.location.reload();
    } else {
      setShowError(true);
    }
  }, [error, pathname, chunkLoad]);

  if (!showError) {
    return null;
  }

  return (
    <div className="safe-full-screen w-screen flex flex-col justify-center items-center text-white px-6 text-center">
      <img src={logo} alt={t('{{brand}}')} width={56} height={56} />
      <h1 className="text-2xl font-bold mt-6">{t('link_out_unreachable.title')}</h1>
      <p className="text-md mt-3 mb-8 opacity-80">{t('link_out_unreachable.body')}</p>
      <div className="w-full max-w-sm">
        <ActionButton title={t('link_out_unreachable.try_again')} onClick={() => window.location.reload()} />
      </div>
    </div>
  );
}

/**
 * Catches a route that fails to render, most often a lazy route whose chunk didn't download: the
 * connection dropped, or a deploy replaced the files since the page loaded. Without it React unmounts
 * the whole page, which after a purchase (/gift → /gift/success) leaves a paying customer on a blank
 * screen. Screens read their state from the URL, so reloading recovers: a chunk failure reloads by
 * itself once (see `claimAutoReload`), anything else shows Try again. Keyed by path so going back or
 * navigating elsewhere renders normally again.
 */
export const RouteErrorBoundary: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {pathname} = useLocation();
  return (
    <Sentry.ErrorBoundary
      key={pathname}
      // Lets alert rules tell a stale tab after a deploy from a screen that crashed.
      beforeCapture={(scope, error) => scope.setTag('chunk_load', String(isChunkLoadError(error)))}
      fallback={({error}) => <RouteLoadFailedScreen error={error} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
